import {useEffect, useState} from 'react';
import {AccessibilityInfo, findNodeHandle} from 'react-native';

/**
 * Custom hook to manage accessibility focus for screen readers.
 * Automatically focuses the provided ref when screen reader is enabled after a specified delay.
 *
 * @param ref - React ref to the element that should receive focus
 * @param delay - Delay in milliseconds before setting focus (default: 1500)
 * @returns isScreenReaderEnabled - Boolean indicating if screen reader is currently enabled
 */
export function useAccessibilityFocus(
  ref: React.RefObject<any>,
  delay: number = 1500,
): boolean {
  const [isScreenReaderEnabled, setIsScreenReaderEnabled] = useState(false);

  useEffect(() => {
    // Initial check
    AccessibilityInfo.isScreenReaderEnabled().then(enabled => {
      setIsScreenReaderEnabled(enabled);
    });

    // Listen for changes
    const subscription = AccessibilityInfo.addEventListener(
      'screenReaderChanged',
      enabled => {
        setIsScreenReaderEnabled(enabled);
      },
    );

    return () => {
      subscription?.remove();
    };
  }, []);

  useEffect(() => {
    if (isScreenReaderEnabled) {
      const timeout = setTimeout(() => {
        if (ref.current) {
          const nodeHandle = findNodeHandle(ref.current);
          if (nodeHandle) {
            AccessibilityInfo.setAccessibilityFocus(nodeHandle);
          }
        }
      }, delay);

      return () => clearTimeout(timeout);
    }
  }, [isScreenReaderEnabled, ref, delay]);

  return isScreenReaderEnabled;
}
